import { Link, useRouterState } from "@tanstack/react-router";
import {
  MessageSquare,
  Boxes,
  Workflow,
  History,
  BookOpen,
  Plug,
  Network,
  Server,
  ClipboardList,
  FileCode,
  Bookmark,
  Settings,
  Wand2,
} from "lucide-react";
import logo from "@/assets/logo-color.png";
import { useCompanion } from "@/state/CompanionContext";

type Item = { label: string; to: string; icon: React.ComponentType<{ className?: string }> };

const sections: { label: string; items: Item[] }[] = [
  {
    label: "Workshop",
    items: [
      { label: "Copilot", to: "/copilot", icon: MessageSquare },
      { label: "Macro Builder", to: "/builder", icon: Wand2 },
      { label: "Macro Catalog", to: "/catalog", icon: Boxes },
      { label: "Workflows", to: "/workflows", icon: Workflow },
      { label: "Run History", to: "/runs", icon: History },
      { label: "Knowledge Base", to: "/knowledge", icon: BookOpen },
    ],
  },
  {
    label: "Platform",
    items: [
      { label: "CAD Integrations", to: "/integrations", icon: Plug },
      { label: "Teamcenter Bridge", to: "/teamcenter", icon: Network },
      { label: "Execution Hosts", to: "/hosts", icon: Server },
      { label: "Audit Log", to: "/audit", icon: ClipboardList },
    ],
  },
  {
    label: "Personal",
    items: [
      { label: "My Macros", to: "/my-macros", icon: FileCode },
      { label: "Saved Workflows", to: "/saved-workflows", icon: Bookmark },
      { label: "Settings", to: "/settings", icon: Settings },
    ],
  },
];

export function AppSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { goHome } = useCompanion();

  return (
    <aside className="flex h-screen w-[220px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      {/* Brand */}
      <div className="flex flex-col items-center gap-2 border-b border-sidebar-border px-4 pt-5 pb-4">
        <img
          src={logo}
          alt="Joules to Watts"
          className="h-14 w-auto object-contain mix-blend-screen"
        />
        <div className="text-center text-[13px] font-semibold tracking-tight text-white">
          Macro Studio
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section, i) => (
          <div
            key={section.label}
            className={i > 0 ? "mt-4 border-t border-sidebar-border pt-4" : ""}
          >
            <div className="px-2 pb-1.5 text-[10.5px] font-medium tracking-wider text-sidebar-muted uppercase">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active =
                  pathname === item.to ||
                  pathname.startsWith(item.to + "/") ||
                  (item.to === "/copilot" && pathname === "/");
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={item.to === "/copilot" ? () => goHome() : undefined}
                      className={`relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] transition-colors duration-150 ${
                        active
                          ? "bg-sidebar-active font-medium text-white"
                          : "text-sidebar-foreground hover:bg-sidebar-hover hover:text-white"
                      }`}
                    >
                      {active && (
                        <span className="bg-primary absolute top-1 bottom-1 left-0 w-[2px] rounded-full" />
                      )}
                      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Identity */}
      <div className="flex items-center gap-2.5 border-t border-sidebar-border px-3 py-3">
        <div className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-medium">
          AC
        </div>
        <div className="min-w-0">
          <div className="text-sidebar-foreground truncate text-[13px] font-medium">
            Alex Carter
          </div>
          <div className="text-sidebar-muted truncate text-[11px]">Avantek · Body & Chassis</div>
        </div>
      </div>
    </aside>
  );
}
